import React, { useEffect, useRef, useState } from 'react';

import { Avatar, Button, Card, CardBody, CardHeader, Link, Separator, Text } from '@change/ds-core-ui';
import { ArrowUpIcon } from '@change/ds-icons/ArrowUpIcon';
import { EndorserBorderIcon } from '@change/ds-icons/EndorserBorderIcon';
import { cn } from '@change/ds-utils';

import { useI18n, withI18nPlaceholders } from '@change-corgi/core-react/i18n';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/petitionDetails/petition-details.yml';

import { getEndorsementCardElementId } from '#src/app/shared/utils/endorsements';

const LINE_CLAMP_CLASSES: Record<number, string> = {
	3: 'line-clamp-3',
	7: 'line-clamp-7',
};

const DescriptionSection: React.FC<DescriptionSectionProps> = ({
	description,
	textRef,
	hasExternalHandler,
	showFullDescription,
	expanded,
	shouldShowButton,
	handleToggle,
	getButtonLabel,
	descriptionLineClamp = 3,
}) => {
	const lineClampClass = LINE_CLAMP_CLASSES[descriptionLineClamp];
	return (
		<>
			<Separator orientation="horizontal" className="border-stroke-info-weak my-5 opacity-34" />
			<CardBody className="p-0!">
				<Text
					as="p"
					ref={showFullDescription ? undefined : textRef}
					className={cn('leading-7 break-normal wrap-anywhere whitespace-pre-line', {
						[lineClampClass]: !showFullDescription && (hasExternalHandler || !expanded),
					})}
					expressive
				>
					{description}
				</Text>
				{shouldShowButton && handleToggle && (
					<Button
						onClick={handleToggle}
						className="text-typography-strong mt-3 w-fit p-0"
						data-testid="endorsement-toggle-button"
						variant="tertiary"
						size="x-small"
					>
						<Text variant="body" size="medium" fontWeight="normal" className="text-typography-strong">
							{getButtonLabel()}
						</Text>
					</Button>
				)}
			</CardBody>
		</>
	);
};

// oxlint-disable-next-line max-lines-per-function
const EndorsementCardComponent: React.FC<Props> = ({
	endorsementId,
	name,
	title,
	description,
	photoUrl,
	link,
	onReadMore,
	readMoreLabel,
	showFullDescription = false,
	descriptionLineClamp = 3,
	className,
	handleEndorserClick,
}) => {
	const textRef = useRef<HTMLParagraphElement>(null);
	const [isClamped, setIsClamped] = useState(false);
	const [expanded, setExpanded] = useState(false);

	const hasExternalHandler = Boolean(onReadMore);
	const { translate } = useI18n();

	const checkClamped = React.useCallback(() => {
		if (showFullDescription) return;
		const descriptionElement = textRef.current;
		if (!descriptionElement) return;
		const clamped = descriptionElement.scrollHeight > descriptionElement.clientHeight + 1;
		setIsClamped(clamped);
	}, [showFullDescription]);

	useEffect(() => {
		checkClamped();
	}, [description, showFullDescription, checkClamped]);

	useEffect(() => {
		const descriptionElement = textRef.current;
		if (!descriptionElement || showFullDescription) {
			return undefined;
		}

		const resizeObserver = new ResizeObserver(() => {
			checkClamped();
		});
		resizeObserver.observe(descriptionElement);
		return () => {
			resizeObserver.disconnect();
		};
	}, [description, showFullDescription, checkClamped]);

	const handleToggle = hasExternalHandler ? onReadMore : () => setExpanded((prev) => !prev);

	const getButtonLabel = () => {
		if (hasExternalHandler && readMoreLabel) {
			return readMoreLabel;
		}
		return expanded
			? translate('corgi.petition_details.endorsements.show_less')
			: translate('corgi.petition_details.endorsements.read_full');
	};

	const shouldShowButton = (() => {
		if (showFullDescription) {
			return false;
		}
		if (hasExternalHandler) {
			return isClamped && Boolean(onReadMore && readMoreLabel);
		}
		return isClamped || expanded;
	})();

	const nameContent = (
		<Text variant="body" fontWeight="bold" size="large" className="leading-7">
			{name}
		</Text>
	);

	return (
		<Card
			id={endorsementId ? getEndorsementCardElementId(endorsementId) : undefined}
			className={cn('gap-0! p-5!', className || 'bg-fill-information-weaker border-none p-6!')}
		>
			<CardHeader className="flex items-start justify-between p-0!">
				<div className="flex items-center gap-4">
					<div className="relative flex h-[68px] w-[68px] items-center justify-center">
						<EndorserBorderIcon size={68} className="text-icon-information" />

						<Avatar
							// eslint-disable-next-line @change-ds/ds-no-raw-color-classname -- white gap between photo and endorser badge border in both modes (no fixed stroke token)
							className="absolute min-h-[52px] min-w-[52px] border-2 border-white"
							size={52}
							alt={name}
							src={photoUrl}
						/>
					</div>
					<div className="flex flex-col justify-between">
						{link ? (
							<Link href={link} onClick={handleEndorserClick ?? undefined} variant="secondary" target="_blank">
								<div className="flex flex-row gap-1.5">
									{nameContent}
									<div className="rotate-45">
										<ArrowUpIcon size={12} data-testid="arrow-up-icon" />
									</div>
								</div>
							</Link>
						) : (
							nameContent
						)}
						<Text variant="body" size="medium" fontWeight="normal" className="text-typography-strong mt-0.5">
							{title}
						</Text>
					</div>
				</div>
			</CardHeader>
			{description && (
				<DescriptionSection
					description={description}
					textRef={textRef}
					hasExternalHandler={hasExternalHandler}
					showFullDescription={showFullDescription}
					expanded={expanded}
					shouldShowButton={shouldShowButton}
					handleToggle={handleToggle}
					getButtonLabel={getButtonLabel}
					descriptionLineClamp={descriptionLineClamp}
				/>
			)}
		</Card>
	);
};

type Props = {
	endorsementId?: string;
	name: string;
	title: string;
	description?: string;
	photoUrl: string;
	link?: string;
	onReadMore?: () => void;
	readMoreLabel?: string;
	showFullDescription?: boolean;
	descriptionLineClamp?: 3 | 7;
	className?: string;
	handleEndorserClick?: () => void;
};

type DescriptionSectionProps = {
	description: string;
	textRef: React.RefObject<HTMLParagraphElement | null>;
	hasExternalHandler: boolean;
	showFullDescription: boolean;
	expanded: boolean;
	shouldShowButton: boolean;
	handleToggle: (() => void) | undefined;
	getButtonLabel: () => string;
	descriptionLineClamp?: 3 | 7;
};

export const EndorsementCard = withI18nPlaceholders(I18N_PLACEHOLDERS, EndorsementCardComponent);
